import { useState, useMemo, useEffect } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRoute, useLocation, Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import EditCompanyModal from "@/components/modals/edit-company-modal";
import FMCSACarriersBulkModal from "@/components/modals/fmcsa-carriers-bulk-modal";
import CreateCarrierModal from "@/components/modals/create-carrier-modal";
import CreateWorkOrderModal from "@/components/modals/create-work-order-modal";
import DOTCompanyRefreshModal from "@/components/modals/dot-company-refresh-modal";
import NotesSection from "@/components/notes/notes-section";
import CompanyContactsSection from "@/components/company-contacts/company-contacts-section";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { 
  Building2, Mail, Phone, MapPin, User, Edit, Truck, FileText, 
  Plus, Shield, Search, Calendar, AlertCircle, 
  Users, ExternalLink, CreditCard, Hash, RefreshCw, Key, Download
} from "lucide-react";
import { format } from "date-fns";
import { formatDateOnly } from "@/lib/dateUtils";
import AuditInfo from "@/components/audit-info";
import { track } from "@/lib/analytics";
import { formatPhoneNumber } from "@/lib/utils";

export default function CompanyDetailsPage() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [, setLocation] = useLocation();
  const [match, params] = useRoute("/companies/:id");
  const companyId = params?.id ? parseInt(params.id) : null;
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFMCSABulkModal, setShowFMCSABulkModal] = useState(false);
  const [showCreateCarrierModal, setShowCreateCarrierModal] = useState(false);
  const [showCreateWorkOrderModal, setShowCreateWorkOrderModal] = useState(false);
  const [showDOTRefreshModal, setShowDOTRefreshModal] = useState(false);
  const [showStatusSelect, setShowStatusSelect] = useState(false);
  const [statusHistoryOpen, setStatusHistoryOpen] = useState(true);
  const [workOrderSearchTerm, setWorkOrderSearchTerm] = useState("");
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState("all");
  
  const { data: company, isLoading } = useQuery<any>({
    queryKey: ["/api/companies", companyId],
    enabled: !!companyId,
    retry: false,
  });

  const { data: companyContacts = [] } = useQuery<any[]>({
    queryKey: ["/api/companies", companyId, "contacts"],
    queryFn: async () => {
      const response = await fetch(`/api/companies/${companyId}/contacts`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch contacts");
      return response.json();
    },
    enabled: !!companyId,
  });

  const { data: users = [] } = useQuery<any[]>({
    queryKey: ["/api/assignable-users"],
    enabled: !!companyId,
  });

  const { data: authorityLetters = [] } = useQuery<any[]>({
    queryKey: ["/api/authority-letters", { companyId }],
    queryFn: async () => {
      const response = await fetch(`/api/authority-letters?companyId=${companyId}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch authority letters");
      const body = await response.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!companyId,
  });

  const { data: statusHistory = [] } = useQuery<any[]>({
    queryKey: ["/api/companies", companyId, "status-history"],
    queryFn: async () => {
      const response = await fetch(`/api/companies/${companyId}/status-history`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch status history");
      return response.json();
    },
    enabled: !!companyId,
  });

  // Find the primary contact for this company (uses isPrimary boolean only)
  const primaryContact = useMemo(() => {
    const primaryRelation = companyContacts.find((cc: any) => cc.isPrimary === true);
    return primaryRelation?.contact;
  }, [companyContacts]);

  const companyPageTitle = company
    ? [company.name, company.dotProfile?.dotNumber ? `(DOT:${company.dotProfile.dotNumber})` : null]
        .filter(Boolean).join(' ')
    : null;
  usePageTitle(companyPageTitle);

  const updateCompanyMutation = useMutation({
    mutationFn: async ({ companyData, officersToCreate }: { companyData: any; officersToCreate?: Array<{ firstName: string; lastName: string; email: string | null }> }) => {
      await apiRequest("PUT", `/api/companies/${companyId}`, companyData);

      if (officersToCreate && officersToCreate.length > 0) {
        const existingContactsRes = await apiRequest("GET", `/api/companies/${companyId}/contacts`);
        const existingContacts: any[] = existingContactsRes.ok ? await existingContactsRes.json() : [];

        let createdCount = 0;
        for (const officer of officersToCreate) {
          const isDuplicate = existingContacts.some((c: any) => {
            const contact = c.contact || c;
            const fName = (contact.firstName || "").trim().toUpperCase();
            const lName = (contact.lastName || "").trim().toUpperCase();
            const cEmail = (contact.email || "").trim().toUpperCase();
            const oFName = (officer.firstName || "").trim().toUpperCase();
            const oLName = (officer.lastName || "").trim().toUpperCase();
            const oEmail = (officer.email || "").trim().toUpperCase();
            const nameMatch = fName === oFName && lName === oLName;
            const emailMatch = oEmail && cEmail && cEmail === oEmail;
            return nameMatch || emailMatch;
          });

          if (isDuplicate) continue;

          const contactData = {
            firstName: officer.firstName,
            lastName: officer.lastName,
            email: officer.email || undefined,
          };

          const contactResponse = await apiRequest("POST", "/api/contacts", contactData);
          const newContact = await contactResponse.json();

          await apiRequest("POST", `/api/contacts/${newContact.id}/companies`, {
            companyId: Number(companyId),
            relationshipType: "owner",
            isPrimary: existingContacts.length === 0 && createdCount === 0,
          });
          existingContacts.push({ contact: { firstName: officer.firstName, lastName: officer.lastName, email: officer.email } });
          createdCount++;
        }
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId, "contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      const officerCount = variables.officersToCreate?.length || 0;
      toast({
        title: "Success",
        description: officerCount > 0
          ? `Company updated successfully with new contact(s) added`
          : "Company updated successfully",
      });
      setShowEditModal(false);
      setShowDOTRefreshModal(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update company",
        variant: "destructive",
      });
    },
  });

  const updateDotProfileMutation = useMutation({
    mutationFn: async (profileData: any) => {
      await apiRequest("PUT", `/api/companies/${companyId}/dot-profile`, profileData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
      toast({
        title: "Success",
        description: "DOT profile updated successfully",
      });
      setShowDOTRefreshModal(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update DOT profile",
        variant: "destructive",
      });
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      await apiRequest("PUT", `/api/companies/${companyId}`, { status: newStatus });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId, "status-history"] });
      toast({ title: "Success", description: "Company status updated" });
      setShowStatusSelect(false);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    },
  });

  const createWorkOrderMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/work-orders", data);
      return await response.json();
    },
    onSuccess: (createdWorkOrder) => {
      track("work_order_created", {
        source_page: `/companies/${companyId}`,
        work_order_id: createdWorkOrder?.id ?? null,
        company_id: createdWorkOrder?.companyId ?? companyId,
        carrier_id: createdWorkOrder?.carrierId ?? null,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      toast({
        title: "Success",
        description: "Work order created successfully",
      });
      setShowCreateWorkOrderModal(false);
      if (createdWorkOrder?.id) {
        setLocation(`/work-orders/${createdWorkOrder.id}`);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create work order",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (showCreateWorkOrderModal) {
      track("work_order_create_opened", {
        source_page: `/companies/${companyId}`,
        company_id: companyId,
      });
    }
  }, [showCreateWorkOrderModal, companyId]);

  const createCarrierMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiRequest("POST", "/api/carriers", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      toast({
        title: "Success",
        description: "Carrier created successfully",
      });
      setShowCreateCarrierModal(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create carrier",
        variant: "destructive",
      });
    },
  });

  const filteredWorkOrders = useMemo(() => {
    if (!company?.workOrders) return [];
    
    return company.workOrders.filter((workOrder: any) => {
      const matchesSearch = workOrderSearchTerm === "" || 
        workOrder.workOrderNumber?.toLowerCase().includes(workOrderSearchTerm.toLowerCase()) ||
        workOrder.serviceType?.toLowerCase().includes(workOrderSearchTerm.toLowerCase());
      
      const matchesStatus = workOrderStatusFilter === "all" || 
        (workOrderStatusFilter === "active" && workOrder.status !== "closed" && workOrder.status !== "cancelled") ||
        workOrder.status === workOrderStatusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [company?.workOrders, workOrderSearchTerm, workOrderStatusFilter]);

  const formatCompanyType = (type: string) => {
    return type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Unknown";
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "client": return "bg-green-100 text-green-800";
      case "lead": return "bg-blue-100 text-blue-800";
      case "inquiry": return "bg-purple-100 text-purple-800";
      case "blocked": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getBillingPreferenceBadge = (preference: string) => {
    switch (preference) {
      case "batch":
        return <Badge className="bg-purple-100 text-purple-800">Batch Billing</Badge>;
      case "pay_at_service":
      default:
        return <Badge className="bg-green-100 text-green-800">Pay at Service</Badge>;
    }
  };

  const handleViewDOTProfile = () => {
    if (company?.dotProfile?.dotNumber) {
      const profileUrl = `https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${company.dotProfile?.dotNumber}`;
      window.open(profileUrl, "_blank");
    }
  };

  if (!match || !companyId) {
    return (
      <ResponsiveLayout 
        title="Company Not Found" 
        subtitle="The requested company could not be found"
        breadcrumbs={[{ label: "Companies", href: "/companies" }, { label: "Not Found" }]}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-lg text-muted-foreground">Company not found</p>
        </div>
      </ResponsiveLayout>
    );
  }

  if (isLoading) {
    return (
      <ResponsiveLayout title="Loading..." subtitle="Fetching company details">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </ResponsiveLayout>
    );
  }

  if (!company) {
    return (
      <ResponsiveLayout 
        title="Company Not Found" 
        subtitle="The requested company could not be found"
        breadcrumbs={[{ label: "Companies", href: "/companies" }, { label: "Not Found" }]}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-lg text-muted-foreground">Company not found</p>
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout 
      title={company.name || "Company Details"} 
      subtitle={company.dotProfile?.dotNumber ? `DOT: ${company.dotProfile?.dotNumber}` : "Company Information"}
      breadcrumbs={[
        { label: "Companies", href: "/companies" },
        { label: company.name || "Company" }
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold" data-testid="text-company-name">{company.name}</h1>
              <p className="text-sm text-muted-foreground">
                {company.dotProfile?.dotNumber ? `DOT: ${company.dotProfile?.dotNumber}` : "No DOT number"}
              </p>
              <AuditInfo
                createdAt={company.createdAt}
                createdBy={company.createdByUser}
                updatedAt={company.updatedAt}
                updatedBy={company.updatedByUser}
              />
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {(currentUser?.role === 'admin' || currentUser?.role === 'staff') && showStatusSelect ? (
              <div className="flex items-center gap-2">
                <Select
                  value={company.status}
                  onValueChange={(val) => changeStatusMutation.mutate(val)}
                  disabled={changeStatusMutation.isPending}
                >
                  <SelectTrigger className="h-7 text-sm w-32" data-testid="select-inline-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inquiry">Inquiry</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowStatusSelect(false)}>Cancel</Button>
              </div>
            ) : (
              <Badge
                className={`${getStatusColor(company.status)} ${(currentUser?.role === 'admin' || currentUser?.role === 'staff') ? 'cursor-pointer' : ''}`}
                data-testid="badge-company-status"
                onClick={() => (currentUser?.role === 'admin' || currentUser?.role === 'staff') && setShowStatusSelect(true)}
                title={(currentUser?.role === 'admin' || currentUser?.role === 'staff') ? 'Click to change status' : undefined}
              >
                {company.status || "Unknown"}
              </Badge>
            )}
            <Badge variant="outline" data-testid="badge-company-type">
              {formatCompanyType(company.type)}
            </Badge>
            {company.dotProfile?.dotNumber && (
              <Button 
                variant="outline" 
                onClick={() => setShowDOTRefreshModal(true)} 
                data-testid="button-refresh-dot"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh DOT Profile
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowEditModal(true)} data-testid="button-edit-company">
              <Edit className="w-4 h-4 mr-2" />
              Edit Company
            </Button>
          </div>
          {company.dotProfile?.dotNumber && company.dotProfile?.lastFetchedAt && (
            <p className="text-xs text-muted-foreground text-right">
              DOT profile last refreshed {format(new Date(company.dotProfile.lastFetchedAt), "MMM d, yyyy 'at' h:mm a")}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="font-medium">{company.email || "Not provided"}</p>
                    <p className="text-sm text-gray-500">Email</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="font-medium">{formatPhoneNumber(company.phone) || "Not provided"}</p>
                    <p className="text-sm text-gray-500">Phone</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="font-medium">{company.address || "Not provided"}</p>
                    <p className="text-sm text-gray-500">Address</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="font-medium">
                      {primaryContact 
                        ? `${primaryContact.firstName} ${primaryContact.lastName}`
                        : "Not set"
                      }
                    </p>
                    <p className="text-sm text-gray-500">Primary Contact</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Business Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Hash className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{company.ein || "Not provided"}</p>
                      <p className="text-sm text-gray-500">EIN</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="font-medium">{company.dotProfile?.dotNumber || "Not set"}</p>
                      <p className="text-sm text-gray-500">DOT Number</p>
                    </div>
                    {company.dotProfile?.dotNumber && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleViewDOTProfile}
                        className="h-7 px-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Key className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{company.dotPin || "Not set"}</p>
                      <p className="text-sm text-gray-500">DOT PIN</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-gray-500" />
                    <div>
                      {getBillingPreferenceBadge(company.billingPreference)}
                      <p className="text-sm text-gray-500">Billing Preference</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="font-medium">
                      {company.billingEmailSameAsCompany !== false 
                        ? (company.email || "No email set")
                        : (company.billingEmail || "No billing email set")}
                    </p>
                    <p className="text-sm text-gray-500">
                      Billing Email {company.billingEmailSameAsCompany !== false && "(same as company)"}
                    </p>
                  </div>
                </div>
                {company.dotProfile?.dbaName && (
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{company.dotProfile?.dbaName}</p>
                      <p className="text-sm text-gray-500">DBA Name</p>
                    </div>
                  </div>
                )}
                <Separator />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-500">Login.gov Credentials</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="font-medium text-sm">{company.loginGovUsername || "Not provided"}</p>
                      <p className="text-xs text-gray-500">Username</p>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{company.loginGovAuthMethod || "Not set"}</p>
                      <p className="text-xs text-gray-500">Auth Method</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Work Orders
                </CardTitle>
                <Button 
                  size="sm" 
                  onClick={() => setShowCreateWorkOrderModal(true)}
                  data-testid="button-create-work-order"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Work Order
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!primaryContact && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No primary contact is set for this company. Work orders require a requesting contact. 
                      Please set a primary contact first.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex items-center gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search work orders..."
                      value={workOrderSearchTerm}
                      onChange={(e) => setWorkOrderSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="input-search-work-orders"
                    />
                  </div>
                  <Select value={workOrderStatusFilter} onValueChange={setWorkOrderStatusFilter}>
                    <SelectTrigger className="w-40" data-testid="select-status-filter">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="collecting_inputs">Collecting Inputs</SelectItem>
                      <SelectItem value="validating">Validating</SelectItem>
                      <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                      <SelectItem value="preparing_documents">Preparing Documents</SelectItem>
                      <SelectItem value="awaiting_signatures">Awaiting Signatures</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="awaiting_client_filing">Awaiting Client Filing</SelectItem>
                      <SelectItem value="work_complete">Work Complete</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="max-h-64 overflow-y-auto">
                  {filteredWorkOrders.length > 0 ? (
                    <div className="space-y-2">
                      {filteredWorkOrders.map((workOrder: any) => (
                        <div key={workOrder.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <button
                                onClick={() => setLocation(`/work-orders/${workOrder.id}`)}
                                className="font-medium text-primary hover:text-primary/80 hover:underline text-left"
                                data-testid={`link-work-order-${workOrder.id}`}
                              >
                                {workOrder.workOrderNumber}
                              </button>
                              <p className="text-sm text-gray-500 mt-1">
                                {workOrder.serviceTypeName || workOrder.serviceType?.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>Created: {workOrder.createdAt ? format(new Date(workOrder.createdAt), 'MMM d, yyyy') : 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                            <Badge className={workOrder.status === "completed" ? "bg-green-100 text-green-800" : workOrder.status === "cancelled" ? "bg-gray-100 text-gray-800" : "bg-yellow-100 text-yellow-800"}>
                              {workOrder.status?.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      {company.workOrders && company.workOrders.length > 0 ? "No work orders match your search" : "No work orders found"}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <NotesSection
            entityType="company"
            entityId={companyId?.toString() || ""}
            title="Company Notes"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Carriers
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowFMCSABulkModal(true)}
                    data-testid="button-fetch-fmcsa-carriers"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Fetch from FMCSA
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setShowCreateCarrierModal(true)}
                    data-testid="button-add-carrier"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Carrier
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-64 overflow-y-auto">
                {company.carriers && company.carriers.length > 0 ? (
                  <div className="space-y-2">
                    {company.carriers.map((carrier: any) => (
                      <div key={carrier.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <button
                              onClick={() => setLocation(`/carriers/${carrier.id}`)}
                              className="font-medium text-primary hover:text-primary/80 hover:underline text-left"
                              data-testid={`link-carrier-${carrier.id}`}
                            >
                              {carrier.name}
                            </button>
                            <p className="text-sm text-gray-500 mt-1">
                              {carrier.docketPrefix && carrier.docketNumber 
                                ? `${carrier.docketPrefix}-${carrier.docketNumber}`
                                : "No docket number"
                              }
                            </p>
                          </div>
                          <Badge className={
                            carrier.commonAuthorityStatus === "A" ? "bg-green-100 text-green-800" :
                            carrier.commonAuthorityStatus === "I" ? "bg-red-100 text-red-800" :
                            "bg-gray-100 text-gray-800"
                          }>
                            {carrier.commonAuthorityStatus === "A" ? "Active" :
                             carrier.commonAuthorityStatus === "I" ? "Inactive" :
                             carrier.commonAuthorityStatus || "Unknown"}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No carriers found</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Contacts
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CompanyContactsSection
                companyId={companyId}
                companyName={company.name}
              />
            </CardContent>
          </Card>
        </div>

        {statusHistory.length > 0 && (
          <Collapsible open={statusHistoryOpen} onOpenChange={setStatusHistoryOpen}>
            <Card>
              <CardHeader className="py-3">
                <CollapsibleTrigger asChild>
                  <button className="w-full text-left flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Status History
                    </CardTitle>
                    {statusHistoryOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </button>
                </CollapsibleTrigger>
              </CardHeader>
              <CollapsibleContent>
                <CardContent>
                  <div className="space-y-2">
                    {statusHistory.map((entry: any) => {
                      const tz = /Z$|[+-]\d{2}:\d{2}$/.test(entry.changedAt) ? entry.changedAt : `${entry.changedAt}Z`;
                      const d = new Date(tz);
                      const dateStr = isNaN(d.getTime()) ? entry.changedAt : format(d, "MMM d, yyyy 'at' h:mm a");
                      const reasonLabels: Record<string, string> = {
                        work_order_created: 'Work Order Created',
                        payment_received: 'Payment Received',
                        manual: 'Manual Change',
                        initial: 'Initial Status',
                      };
                      const reasonLabel = reasonLabels[entry.reason] || entry.reason;
                      const agentLabel = entry.changedByUserName?.trim() ? entry.changedByUserName.trim() : 'Automatic';
                      return (
                        <div key={entry.id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {entry.fromStatus && (
                              <>
                                <Badge className={`text-xs ${getStatusColor(entry.fromStatus)}`}>{entry.fromStatus}</Badge>
                                <span className="text-gray-400 text-xs">→</span>
                              </>
                            )}
                            <Badge className={`text-xs ${getStatusColor(entry.toStatus)}`}>{entry.toStatus}</Badge>
                          </div>
                          <div className="text-right text-xs text-gray-500">
                            <div>{reasonLabel}</div>
                            <div>{agentLabel} · {dateStr}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {company.dotProfile?.dotNumber && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="w-5 h-5" />
                DOT Profile Data
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-muted-foreground border-b pb-2">Classification</h4>
                  <div>
                    <p className="text-sm text-gray-500">Carrier Operation</p>
                    <p className="font-medium">{company.dotProfile?.carrierOperation || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Class Definition</p>
                    <p className="font-medium">{company.dotProfile?.classDefinition || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Business Organization</p>
                    <p className="font-medium">{company.dotProfile?.businessOrgDescription || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Carship</p>
                    <p className="font-medium">{company.dotProfile?.carship || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Hazmat Indicator</p>
                    <p className="font-medium">{company.dotProfile?.hmIndicator || "N"}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-muted-foreground border-b pb-2">Fleet & Drivers</h4>
                  <div>
                    <p className="text-sm text-gray-500">Power Units</p>
                    <p className="font-medium">{company.dotProfile?.powerUnits || "0"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Truck Units</p>
                    <p className="font-medium">{company.dotProfile?.truckUnits || "0"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Bus Units</p>
                    <p className="font-medium">{company.dotProfile?.busUnits || "0"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fleet Size</p>
                    <p className="font-medium">{company.dotProfile?.fleetSize || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Drivers</p>
                    <p className="font-medium">{company.dotProfile?.totalDrivers || "0"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total CDL</p>
                    <p className="font-medium">{company.dotProfile?.totalCdl || "0"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Interstate Drivers</p>
                    <p className="font-medium">{company.dotProfile?.driverInterTotal || "0"}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-muted-foreground border-b pb-2">Geographic Operations</h4>
                  <div>
                    <p className="text-sm text-gray-500">Interstate Beyond 100 Miles</p>
                    <p className="font-medium">{company.dotProfile?.interstateBeyond100Miles || "0"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Interstate Within 100 Miles</p>
                    <p className="font-medium">{company.dotProfile?.interstateWithin100Miles || "0"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Intrastate Beyond 100 Miles</p>
                    <p className="font-medium">{company.dotProfile?.intrastateBeyond100Miles || "0"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Intrastate Within 100 Miles</p>
                    <p className="font-medium">{company.dotProfile?.intrastateWithin100Miles || "0"}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-muted-foreground border-b pb-2">MCS-150 Information</h4>
                  <div>
                    <p className="text-sm text-gray-500">MCS-150 Date</p>
                    <p className="font-medium">{company.dotProfile?.mcs150Date || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">MCS-150 Mileage</p>
                    <p className="font-medium">{company.dotProfile?.mcs150Mileage ? Number(company.dotProfile?.mcs150Mileage).toLocaleString() : "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">MCS-150 Mileage Year</p>
                    <p className="font-medium">{company.dotProfile?.mcs150MileageYear || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Add Date</p>
                    <p className="font-medium">{company.dotProfile?.addDate || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cargo General Freight</p>
                    <p className="font-medium">{company.dotProfile?.cargoGenFreight || "N"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cargo Log/Pole</p>
                    <p className="font-medium">{company.dotProfile?.cargoLogPole || "N"}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Authority Letters Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Authority Letters
              </CardTitle>
              {authorityLetters.length > 0 && (
                <Link href={`/authority-letters?companyId=${companyId}`}>
                  <Button variant="outline" size="sm" data-testid="link-view-all-letters">
                    View All
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {authorityLetters.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {authorityLetters.slice(0, 5).map((letter: any) => (
                  <div key={letter.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <Badge className={
                        letter.letterType === "Authority Grant" ? "bg-green-100 text-green-800 border-green-200" :
                        letter.letterType === "Reinstatement" ? "bg-blue-100 text-blue-800 border-blue-200" :
                        letter.letterType === "Name Change" ? "bg-purple-100 text-purple-800 border-purple-200" :
                        letter.letterType === "Transfer" ? "bg-amber-100 text-amber-800 border-amber-200" :
                        "bg-gray-100 text-gray-800 border-gray-200"
                      }>
                        {letter.letterType || "Unknown"}
                      </Badge>
                      {letter.authorityType && (
                        <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-2 py-0.5 rounded">
                          {letter.authorityType === 'A' ? 'CMO' : 
                           letter.authorityType === 'B' ? 'Contract' : 
                           letter.authorityType === 'C' ? 'Broker' : letter.authorityType}
                        </span>
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium text-sm">{letter.docketFull || "-"}</span>
                        {letter.usdotNumber && (
                          <span className="text-xs text-muted-foreground">DOT: {letter.usdotNumber}</span>
                        )}
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDateOnly(letter.serviceDate, "MMM d, yyyy")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {letter.workOrder && (
                        <Link href={`/work-orders/${letter.workOrder.id}`} onClick={(e) => e.stopPropagation()}>
                          <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                            WO #{letter.workOrder.id}
                          </Badge>
                        </Link>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await fetch(`/api/authority-letters/${letter.id}/download`, { credentials: "include" });
                            if (!response.ok) throw new Error("Download failed");
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `authority-letter-${letter.docketFull || letter.id}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            toast({ title: "Error", description: "Failed to download letter", variant: "destructive" });
                          }
                        }}
                        title="Download letter"
                        data-testid={`button-download-${letter.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {authorityLetters.length > 5 && (
                  <p className="text-sm text-muted-foreground text-center pt-2">
                    +{authorityLetters.length - 5} more letters
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No authority letters found</p>
            )}
          </CardContent>
        </Card>
      </div>

      {showEditModal && (
        <EditCompanyModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={(data, officersToCreate) => {
            updateCompanyMutation.mutate({ companyData: data, officersToCreate });
          }}
          isLoading={updateCompanyMutation.isPending}
          company={company}
        />
      )}

      {showFMCSABulkModal && (
        <FMCSACarriersBulkModal
          open={showFMCSABulkModal}
          onClose={() => setShowFMCSABulkModal(false)}
          companyId={companyId}
          companyName={company.name}
          dotNumber={company.dotProfile?.dotNumber}
          existingCarriers={company.carriers || []}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
            setShowFMCSABulkModal(false);
            toast({
              title: "Success",
              description: "Carriers updated from FMCSA",
            });
          }}
        />
      )}

      {showCreateWorkOrderModal && (
        <CreateWorkOrderModal
          onClose={() => setShowCreateWorkOrderModal(false)}
          onSubmit={(data) => createWorkOrderMutation.mutate(data)}
          isLoading={createWorkOrderMutation.isPending}
          users={users}
          currentUserId={currentUser?.id}
          prePopulate={{
            companyId: companyId || undefined,
            contactId: primaryContact?.id,
            contactName: primaryContact ? `${primaryContact.firstName} ${primaryContact.lastName}` : undefined,
          }}
        />
      )}

      {showCreateCarrierModal && (
        <CreateCarrierModal
          onClose={() => setShowCreateCarrierModal(false)}
          onSubmit={(data) => createCarrierMutation.mutate(data)}
          isLoading={createCarrierMutation.isPending}
          companies={company ? [company] : []}
          defaultCompanyId={companyId || undefined}
        />
      )}

      {showDOTRefreshModal && (
        <DOTCompanyRefreshModal
          open={showDOTRefreshModal}
          onClose={() => setShowDOTRefreshModal(false)}
          company={{
            id: company.id,
            name: company.name,
            email: company.email,
            phone: company.phone,
            address: company.address,
            mailingAddress: company.mailingAddress,
            dotNumber: company.dotProfile?.dotNumber,
            dbaName: company.dotProfile?.dbaName,
            carrierOperation: company.dotProfile?.carrierOperation,
            classDefinition: company.dotProfile?.classDefinition,
            carship: company.dotProfile?.carship,
            powerUnits: company.dotProfile?.powerUnits,
            truckUnits: company.dotProfile?.truckUnits,
            busUnits: company.dotProfile?.busUnits,
            totalDrivers: company.dotProfile?.totalDrivers,
            totalCdl: company.dotProfile?.totalCdl,
            driverInterTotal: company.dotProfile?.driverInterTotal,
            fleetSize: company.dotProfile?.fleetSize,
            hmIndicator: company.dotProfile?.hmIndicator,
            mcs150Date: company.dotProfile?.mcs150Date,
            mcs150Mileage: company.dotProfile?.mcs150Mileage,
            mcs150MileageYear: company.dotProfile?.mcs150MileageYear,
            addDate: company.dotProfile?.addDate,
            interstateBeyond100Miles: company.dotProfile?.interstateBeyond100Miles,
            interstateWithin100Miles: company.dotProfile?.interstateWithin100Miles,
            intrastateBeyond100Miles: company.dotProfile?.intrastateBeyond100Miles,
            intrastateWithin100Miles: company.dotProfile?.intrastateWithin100Miles,
            businessOrgDescription: company.dotProfile?.businessOrgDescription,
            cargoGenFreight: company.dotProfile?.cargoGenFreight,
            cargoLogPole: company.dotProfile?.cargoLogPole,
          }}
          onFetchSuccess={async (rawResponse) => {
            const dotNum = company.dotProfile?.dotNumber || (company as any).dotNumber;
            if (!dotNum) return;
            try {
              await apiRequest("PUT", `/api/companies/${companyId}/dot-profile`, {
                dotNumber: dotNum,
                rawResponse,
              });
              queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
            } catch (e) {
            }
          }}
          onApply={(data) => {
            const { rawResponse, dotNumber: fetchedDotNumber, ...fields } = data as any;
            const dotNum = fetchedDotNumber || company.dotProfile?.dotNumber || (company as any).dotNumber;
            const companyFields: any = {};
            const dotProfileFields: any = { dotNumber: dotNum };
            const dotProfileKeys = [
              'carrierOperation', 'classDefinition', 'carship', 'powerUnits', 'truckUnits',
              'busUnits', 'totalDrivers', 'totalCdl', 'driverInterTotal', 'fleetSize',
              'hmIndicator', 'mcs150Date', 'mcs150Mileage', 'mcs150MileageYear', 'addDate',
              'interstateBeyond100Miles', 'interstateWithin100Miles', 'intrastateBeyond100Miles',
              'intrastateWithin100Miles', 'businessOrgDescription', 'cargoGenFreight', 'cargoLogPole',
              'dbaName'
            ];
            
            Object.entries(fields).forEach(([key, value]) => {
              if (dotProfileKeys.includes(key)) {
                dotProfileFields[key] = value;
              } else {
                companyFields[key] = value;
              }
            });
            
            if (Object.keys(companyFields).length > 0) {
              updateCompanyMutation.mutate({ companyData: companyFields });
            }
            if (dotNum && Object.keys(dotProfileFields).length > 1) {
              if (rawResponse) {
                dotProfileFields.rawResponse = rawResponse;
              }
              updateDotProfileMutation.mutate(dotProfileFields);
            }
          }}
          isUpdating={updateCompanyMutation.isPending || updateDotProfileMutation.isPending}
        />
      )}
    </ResponsiveLayout>
  );
}
